// @ts-nocheck
// import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge"; // Starter Custom Code
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getIsProductCompareFull,
    getProductCompareChecked,
} from "@insite/client-framework/Store/Components/CompareProductsDrawer/CompareProductsDrawerSelectors";
import addProductToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/AddProductToCompare";
import removeProductFromCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/RemoveProductFromCompare";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { BadgeTypeValues } from "@insite/client-framework/Types/ApiModels";
// import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage"; // Starter Custom Code
// import Badge from "@insite/mobius/Badge"; // Starter Custom Code
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import constants from "@store/constants";
import Badge, { BadgeProps } from "@store/MobiusOverrides/Badge/Badge";
import ProductImage, { ProductImageStyles } from "@store/Overrides/Components/ProductImage";
import { productCardProps } from "@store/Themes/common/props/productCard";
import useMergeProps from "@toolkit/Framework/Common/Hooks/useMergeProps";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import useOverlayBadge from "@toolkit/Modules/client-framework/Common/Hooks/useOverlayBadge";
import { spacingStyles } from "@toolkit/Themes/common/mixins";

/* Store Custom Code - Start */
import { useProductListProductContext } from "@store/Framework/Components/ProductListProductContext/ProductListProductContext";
import { useCallback } from "react";
/* Store Custom Code - End */
export interface ProductListProductImageProps extends ProductListProductImageStyles {
    badgeProps?: BadgeProps;
}

/* Starter Custom Code - End */

interface OwnProps {
    showImage: boolean;
    showCompare: boolean;
    extendedStyles?: ProductListProductImageProps; // Starter Custom Code
    showTextBadge?: boolean;
    showImageBadge?: boolean;
}

const mapStateToProps = (state: ApplicationState, props: HasProductContext) => ({
    settingsCollection: getSettingsCollection(state),
    productCompareChecked: getProductCompareChecked(state, props.productContext),
    isProductCompareFull: getIsProductCompareFull(state),
    productToCompareLength: state.components.compareProductsDrawer.productsIds.length, // Starter Custom Code
});

const mapDispatchToProps = {
    addProductToCompare,
    removeProductFromCompare,
};

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps &
    HasProductContext;

export interface ProductListProductImageStyles {
    wrapper?: InjectableCss;
    productImage?: ProductImageStyles;
    compareCheckbox?: CheckboxPresentationProps;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    // Starter Custom Code
    badgeProductImageInOverlay?: ProductImageStyles;
}

export const productImageStyles: ProductListProductImageStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
        `,
    },
    productImage: productCardProps.image,
    badgeAreaWrapper: mergeProps<ProductListProductImageStyles["badgeAreaWrapper"]>(productCardProps.badgeAreaWrapper, {
        css: productCardProps?.image?.wrapper?.css,
    }),
    badgeTextWrapper: productCardProps.badgeTextWrapper,
    badgeText: productCardProps.badgeText,
    badgeImageWrapper: productCardProps.badgeImageWrapper,
    badgeProductImageInOverlay: mergeProps<ProductListProductImageStyles["badgeProductImageInOverlay"]>(
        productCardProps.image,
        {
            wrapper: {
                css: css`
                    margin-bottom: 0;
                `,
            },
        },
    ),
};

const ProductListProductImage = ({
    productContext,
    showImage,
    showTextBadge,
    showImageBadge,
    showCompare,
    isProductCompareFull,
    settingsCollection,
    productCompareChecked,
    extendedStyles,
    addProductToCompare,
    removeProductFromCompare,
    // Starter Custom Code
    productToCompareLength,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const styles = useMergeProps(productImageStyles, extendedStyles); // Starter Custom Code

    const {
        showOverlayBadge,
        overlayBadge,
        overlayBadgeWrapperStyles,
        badgeAreaWrapperRef,
        overlayBadgeWrapperClickHandler,
    } = useOverlayBadge({
        productContext,
        showTextBadge,
        showImageBadge,
        styles,
    });

    /* Store Custom Code - Start */
    const productListProductContext = useProductListProductContext();
    const handleImageClick = useCallback(() => {
        productListProductContext?.onImageClick?.(true);
    }, [productListProductContext?.onImageClick]);
    /* Store Custom Code - End */

    const handleChanged = (_: React.SyntheticEvent, value: boolean) => {
        if (value && productToCompareLength >= constants.productCompare.maxNumberOfColumns /* Starter Custom Code */) {
            toasterContext.addToast({
                body: siteMessage("ProductCompare_MaximumItemsReached"), // Starter Custom Code
                messageType: "warning",
            });
        } else if (value) {
            addProductToCompare({ productId: productContext.product.id });
        } else {
            removeProductFromCompare({ productId: productContext.product.id });
        }
    };

    return (
        <StyledWrapper
            {...styles.wrapper}
            data-test-selector="productListProductImage"
            data-test-key={productContext.product.id}
        >
            {showImage && !showOverlayBadge && (
                <ProductImage
                    extendedStyles={styles.productImage}
                    product={productContext}
                    // Starter Custom Code
                    size="medium"
                    // Store Custom Code
                    onClick={handleImageClick}
                />
            )}
            {showImage && showOverlayBadge && overlayBadge && (
                <StyledWrapper
                    {...styles.badgeAreaWrapper}
                    ref={badgeAreaWrapperRef}
                    data-test-selector="productListBadge"
                >
                    <ProductImage
                        extendedStyles={styles.badgeProductImageInOverlay} // Starter Custom Code
                        product={productContext}
                        // Starter Custom Code
                        size="medium"
                        // Store Custom Code
                        onClick={handleImageClick}
                    />
                    <StyledWrapper {...overlayBadgeWrapperStyles} onClick={overlayBadgeWrapperClickHandler}>
                        <Badge
                            type={overlayBadge.badgeType}
                            value={
                                overlayBadge.badgeType === "Image"
                                    ? overlayBadge.largeImageBadgePath
                                    : overlayBadge.displayText
                            }
                            badgeTextColor={overlayBadge.textColorHexCode}
                            badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                            badgeTextRounding={overlayBadge.badgeStyle === "Round"} // Starter Custom Code
                            imageAltText={overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name}
                            textBadgeCss={styles.badgeText}
                            imageBadgeProps={styles.badgeImageProps}
                            badgeTextPadding={extendedStyles?.badgeProps?.badgeTextPadding} // Starter Custom Code
                        />
                    </StyledWrapper>
                </StyledWrapper>
            )}
            {showCompare && settingsCollection.productSettings.enableProductComparisons && (
                <Checkbox
                    {...styles.compareCheckbox}
                    checked={productCompareChecked}
                    onChange={handleChanged}
                    data-test-selector="productList_CompareCheckbox"
                    sizeVariant="small" // Starter Custom Code
                >
                    {translate("Compare")}
                </Checkbox>
            )}
        </StyledWrapper>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductListProductImage));
